import { helpers } from "../../../worker/util/index.ts";
import type {
	PlayerRatings,
	Position,
} from "../../../common/types.baseball.ts";
import { COMPOSITE_WEIGHTS } from "../../../common/constants.ts";
import compositeRating from "./compositeRating.ts";

type RatingWeights = Record<string, [number, number]>;

export const infoDefense: Record<Position, RatingWeights> = {
	SP: {
		powerPitcher: [1, 1],
		finessePitcher: [2, 1],
		workhorsePitcher: [1, 1],
	},
	RP: {
		powerPitcher: [1, 1],
		finessePitcher: [2, 1],
	},
	C: {
		catcherDefense: [2, 1],
		arm: [1, 1],
	},
	"1B": {
		firstBaseDefense: [2, 1],
		infieldRange: [0.5, 1],
		groundBallDefense: [1, 1],
		arm: [0.5, 1],
	},
	"2B": {
		infieldRange: [1, 1],
		groundBallDefense: [2, 1],
		arm: [1, 1],
	},
	"3B": {
		infieldRange: [0.5, 1],
		groundBallDefense: [2, 1],
		arm: [2, 1],
	},
	SS: {
		infieldRange: [2, 1],
		groundBallDefense: [2, 1],
		arm: [2, 1],
	},
	LF: {
		outfieldRange: [2, 1],
		flyBallDefense: [2, 1],
		arm: [1, 1],
	},
	CF: {
		outfieldRange: [4, 1],
		flyBallDefense: [3, 1],
		arm: [1, 1],
	},
	RF: {
		outfieldRange: [2, 1],
		flyBallDefense: [2, 1],
		arm: [2, 1],
	},
	DH: {},
};

const infoOffense: RatingWeights = {
	powerHitter: [1, 1],
	contactHitter: [1, 1],
	eye: [1, 1],
	speed: [0.2, 1],
};

// Only computes the composite ratings that are actually needed, since this is called a lot (for every position, every time a player develops)
const getScore = (ratings: PlayerRatings, info: RatingWeights) => {
	let r = 0;
	let sumCoeffs = 0;

	for (const [key, [coeff, power]] of Object.entries(info)) {
		const compositeWeight = COMPOSITE_WEIGHTS[key];
		if (!compositeWeight) {
			throw new Error(`Unknown composite rating "${key}"`);
		}
		const value = compositeRating(
			ratings,
			compositeWeight.ratings,
			compositeWeight.weights,
			false,
		);

		const powerFactor = 100 / 100 ** power;
		r += coeff * powerFactor * value ** power;
		sumCoeffs += coeff;
	}

	r /= sumCoeffs;

	return r;
};

const ovr = (ratings: PlayerRatings, pos?: Position): number => {
	const pos2 = pos ?? (ratings.pos as Position);

	// Offense is not used for pitchers
	const offense =
		pos2 === "RP" || pos2 === "SP" ? 0 : getScore(ratings, infoOffense);
	const defense = getScore(ratings, infoDefense[pos2]);

	// The idea here is some positions are just easier to play (they have constants added to them) and are less important (they max out below 1). This is roughly based on WAR position adjustments.
	let r;
	if (pos2 === "RP") {
		r = 0.9 * defense;
	} else if (pos2 === "SP") {
		r = defense;
	} else if (pos2 === "DH") {
		// Needs to have the same offensive weight as other positions, so roster auto sort can work
		r = 0.7 * offense;
	} else if (pos === "SS") {
		r = 0.7 * offense + 0.315 * defense;
	} else if (pos === "C") {
		r = 0.7 * offense + 0.375 * defense;
	} else if (pos === "CF" || pos === "3B" || pos === "2B") {
		r = 0.7 * offense + 0.05 + 0.2 * defense;
	} else if (pos === "1B") {
		r = 0.7 * offense + 0.04 + 0.2 * defense;
	} else if (pos === "LF") {
		r = 0.7 * offense + 0.0975 + 0.1 * defense;
	} else {
		r = 0.7 * offense + 0.1 + 0.1 * defense;
	}

	r *= 100;

	if (pos2 === "RP" || pos2 === "SP") {
		// Scale 10-90 to 0-100
		r = -10 + (r * 100) / 80;
	} else {
		// Scale more for position players
		r = -20 + (r * 100) / 60;
	}

	// Hack to prevent young players (aka low rated players) from all being labeled as outfielders
	if ((pos === "LF" || pos === "RF") && r < 50) {
		r -= 2;
	}

	r = helpers.bound(Math.round(r), 0, 100);

	return r;
};

export default ovr;
